import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Rocket, Play, RotateCcw, Star, Trash2, Target, Info, Zap,
  ArrowUp, ArrowDown, CornerUpRight, CornerUpLeft, Pause, ChevronUp, Code, Eye, EyeOff
} from 'lucide-react';

const GRID_SIZE = 12;
const CELL_SIZE = 40;

const COMMAND_TYPES = [
  { id: 'takeoff', label: 'TAKEOFF', icon: ChevronUp, color: '#22c55e', description: 'Start engines' },
  { id: 'forward', label: 'FORWARD', icon: ArrowUp, color: '#60a5fa', description: 'Move forward 1 cell' },
  { id: 'left', label: 'TURN LEFT', icon: CornerUpLeft, color: '#a78bfa', description: 'Turn 90 left' },
  { id: 'right', label: 'TURN RIGHT', icon: CornerUpRight, color: '#f97316', description: 'Turn 90 right' },
  { id: 'land', label: 'LAND', icon: ArrowDown, color: '#ef4444', description: 'Land the drone' },
];

const MISSIONS = [
  {
    id: 1, name: 'First Flight', description: 'Fly straight up to reach the green checkpoint',
    start: { x: 1, y: 10, dir: 0 },
    checkpoints: [{ x: 1, y: 4 }],
    obstacles: [],
    stars: 1,
    code: {
      Python: `# Mission 1: First Flight
# Fly straight to the checkpoint

drone.takeoff()
for i in range(6):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 1: First Flight
// Fly straight to the checkpoint

drone.takeoff();
for (let i = 0; i < 6; i++) {
  drone.forward();
}
drone.land();`,
      Lua: `-- Mission 1: First Flight
-- Fly straight to the checkpoint

drone:takeoff()
for i = 1, 6 do
  drone:forward()
end
drone:land()`,
    },
  },
  {
    id: 2, name: 'Turn & Go', description: 'Navigate the L-shaped path by turning right',
    start: { x: 1, y: 10, dir: 0 },
    checkpoints: [{ x: 6, y: 5 }],
    obstacles: [
      { x: 2, y: 5 }, { x: 2, y: 4 }, { x: 2, y: 3 },
      { x: 3, y: 5 }, { x: 4, y: 5 }, { x: 5, y: 5 },
    ],
    stars: 2,
    code: {
      Python: `# Mission 2: Turn & Go
# Navigate the L-shaped path

drone.takeoff()
for i in range(5):
    drone.forward()
drone.turn_right()
for i in range(5):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 2: Turn & Go
// Navigate the L-shaped path

drone.takeoff();
for (let i = 0; i < 5; i++) {
  drone.forward();
}
drone.turnRight();
for (let i = 0; i < 5; i++) {
  drone.forward();
}
drone.land();`,
      Lua: `-- Mission 2: Turn & Go
-- Navigate the L-shaped path

drone:takeoff()
for i = 1, 5 do
  drone:forward()
end
drone:turn_right()
for i = 1, 5 do
  drone:forward()
end
drone:land()`,
    },
  },
  {
    id: 3, name: 'Obstacle Course', description: 'Navigate around walls to reach the target',
    start: { x: 1, y: 10, dir: 0 },
    checkpoints: [{ x: 10, y: 2 }],
    obstacles: [
      { x: 3, y: 1 }, { x: 3, y: 2 }, { x: 3, y: 3 }, { x: 3, y: 4 }, { x: 3, y: 5 }, { x: 3, y: 6 }, { x: 3, y: 7 },
      { x: 7, y: 4 }, { x: 7, y: 5 }, { x: 7, y: 6 }, { x: 7, y: 7 }, { x: 7, y: 8 }, { x: 7, y: 9 }, { x: 7, y: 10 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 3: Obstacle Course
# Navigate around walls to the target

drone.takeoff()
# Go up past first wall
for i in range(8):
    drone.forward()
# Turn right, pass the gap
drone.turn_right()
for i in range(5):
    drone.forward()
# Turn down, go past second wall
drone.turn_right()
for i in range(4):
    drone.forward()
# Turn right to target
drone.turn_right()
for i in range(4):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 3: Obstacle Course
// Navigate around walls to the target

drone.takeoff();
// Go up past first wall
for (let i = 0; i < 8; i++) drone.forward();
// Turn right, pass the gap
drone.turnRight();
for (let i = 0; i < 5; i++) drone.forward();
// Turn down, go past second wall
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
// Turn right to target
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 3: Obstacle Course
-- Navigate around walls to the target

drone:takeoff()
-- Go up past first wall
for i = 1, 8 do drone:forward() end
-- Turn right, pass the gap
drone:turn_right()
for i = 1, 5 do drone:forward() end
-- Turn down, go past second wall
drone:turn_right()
for i = 1, 4 do drone:forward() end
-- Turn right to target
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 4, name: 'Maze Runner', description: 'Find the path through the maze',
    start: { x: 0, y: 11, dir: 0 },
    checkpoints: [{ x: 11, y: 0 }],
    obstacles: [
      { x: 2, y: 0 }, { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 }, { x: 2, y: 4 },
      { x: 4, y: 7 }, { x: 4, y: 8 }, { x: 4, y: 9 }, { x: 4, y: 10 }, { x: 4, y: 11 },
      { x: 6, y: 0 }, { x: 6, y: 1 }, { x: 6, y: 2 }, { x: 6, y: 3 }, { x: 6, y: 4 }, { x: 6, y: 5 },
      { x: 9, y: 5 }, { x: 9, y: 6 }, { x: 9, y: 7 }, { x: 9, y: 8 }, { x: 9, y: 9 }, { x: 9, y: 10 }, { x: 9, y: 11 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 4: Maze Runner
# Navigate the maze corridors

drone.takeoff()
# Go up through first corridor
for i in range(6):
    drone.forward()
drone.turn_right()
for i in range(3):
    drone.forward()
# Navigate middle section
drone.turn_left()
for i in range(5):
    drone.forward()
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(5):
    drone.forward()
drone.turn_right()
for i in range(3):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 4: Maze Runner
// Navigate the maze corridors

drone.takeoff();
for (let i = 0; i < 6; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 3; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 4: Maze Runner
-- Navigate the maze corridors

drone:takeoff()
for i = 1, 6 do drone:forward() end
drone:turn_right()
for i = 1, 3 do drone:forward() end
drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:turn_right()
for i = 1, 3 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 5, name: 'Multi-Checkpoint', description: 'Hit all 3 checkpoints in order',
    start: { x: 0, y: 11, dir: 0 },
    checkpoints: [{ x: 0, y: 5 }, { x: 5, y: 5 }, { x: 5, y: 0 }],
    obstacles: [],
    stars: 2,
    code: {
      Python: `# Mission 5: Multi-Checkpoint
# Visit 3 checkpoints in order

drone.takeoff()
# Checkpoint 1: go up
for i in range(6):
    drone.forward()
# Checkpoint 2: turn right, go east
drone.turn_right()
for i in range(5):
    drone.forward()
# Checkpoint 3: turn left, go up
drone.turn_left()
for i in range(5):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 5: Multi-Checkpoint
// Visit 3 checkpoints in order

drone.takeoff();
// Checkpoint 1: go up
for (let i = 0; i < 6; i++) drone.forward();
// Checkpoint 2: turn right, go east
drone.turnRight();
for (let i = 0; i < 5; i++) drone.forward();
// Checkpoint 3: turn left, go up
drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 5: Multi-Checkpoint
-- Visit 3 checkpoints in order

drone:takeoff()
-- Checkpoint 1: go up
for i = 1, 6 do drone:forward() end
-- Checkpoint 2: turn right, go east
drone:turn_right()
for i = 1, 5 do drone:forward() end
-- Checkpoint 3: turn left, go up
drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 6, name: 'Zigzag Path', description: 'Navigate a zigzag corridor between obstacles',
    start: { x: 0, y: 11, dir: 0 },
    checkpoints: [{ x: 10, y: 1 }],
    obstacles: [
      { x: 3, y: 8 }, { x: 3, y: 9 }, { x: 3, y: 10 }, { x: 3, y: 11 },
      { x: 5, y: 0 }, { x: 5, y: 1 }, { x: 5, y: 2 }, { x: 5, y: 3 }, { x: 5, y: 4 }, { x: 5, y: 5 },
      { x: 8, y: 6 }, { x: 8, y: 7 }, { x: 8, y: 8 }, { x: 8, y: 9 }, { x: 8, y: 10 }, { x: 8, y: 11 },
    ],
    stars: 2,
    code: {
      Python: `# Mission 6: Zigzag Path
# Navigate between obstacles in a zigzag

drone.takeoff()
for i in range(4):
    drone.forward()
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(5):
    drone.forward()
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(3):
    drone.forward()
drone.turn_right()
for i in range(2):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 6: Zigzag Path
drone.takeoff();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 2; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 6: Zigzag Path
drone:takeoff()
for i = 1, 4 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 3 do drone:forward() end
drone:turn_right()
for i = 1, 2 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 7, name: 'Spiral Inward', description: 'Fly in a decreasing spiral toward center',
    start: { x: 0, y: 11, dir: 0 },
    checkpoints: [{ x: 5, y: 5 }],
    obstacles: [
      { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 3, y: 0 },
      { x: 11, y: 8 }, { x: 11, y: 9 }, { x: 11, y: 10 }, { x: 11, y: 11 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 7: Spiral Inward
# Fly in a decreasing spiral to the center

drone.takeoff()
# Outer edge: up
for i in range(7):
    drone.forward()
# Turn right, go east
drone.turn_right()
for i in range(6):
    drone.forward()
# Turn right, go down
drone.turn_right()
for i in range(3):
    drone.forward()
# Turn right, go west
drone.turn_right()
for i in range(1):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 7: Spiral Inward
drone.takeoff();
for (let i = 0; i < 7; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 6; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 1; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 7: Spiral Inward
drone:takeoff()
for i = 1, 7 do drone:forward() end
drone:turn_right()
for i = 1, 6 do drone:forward() end
drone:turn_right()
for i = 1, 3 do drone:forward() end
drone:turn_right()
for i = 1, 1 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 8, name: 'Double Wall Dodge', description: 'Navigate around two parallel walls',
    start: { x: 0, y: 6, dir: 1 },
    checkpoints: [{ x: 11, y: 6 }],
    obstacles: [
      { x: 3, y: 0 }, { x: 3, y: 1 }, { x: 3, y: 2 }, { x: 3, y: 3 }, { x: 3, y: 4 }, { x: 3, y: 5 },
      { x: 7, y: 6 }, { x: 7, y: 7 }, { x: 7, y: 8 }, { x: 7, y: 9 }, { x: 7, y: 10 }, { x: 7, y: 11 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 8: Double Wall Dodge
# Start facing east, dodge two walls

drone.takeoff()
# Move east to first wall
for i in range(2):
    drone.forward()
# Go around wall 1 (go down, right, up)
drone.turn_right()
for i in range(2):
    drone.forward()
drone.turn_left()
for i in range(5):
    drone.forward()
# Go around wall 2 (go up, right, down)
drone.turn_left()
for i in range(4):
    drone.forward()
drone.turn_right()
for i in range(4):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 8: Double Wall Dodge
drone.takeoff();
for (let i = 0; i < 2; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 2; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 8: Double Wall Dodge
drone:takeoff()
for i = 1, 2 do drone:forward() end
drone:turn_right()
for i = 1, 2 do drone:forward() end
drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:turn_left()
for i = 1, 4 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 9, name: 'Checkpoint Slalom', description: 'Hit 4 checkpoints weaving through obstacles',
    start: { x: 1, y: 11, dir: 0 },
    checkpoints: [{ x: 1, y: 8 }, { x: 5, y: 5 }, { x: 9, y: 3 }, { x: 11, y: 0 }],
    obstacles: [
      { x: 3, y: 6 }, { x: 3, y: 7 }, { x: 3, y: 8 }, { x: 3, y: 9 },
      { x: 7, y: 2 }, { x: 7, y: 3 }, { x: 7, y: 4 }, { x: 7, y: 5 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 9: Checkpoint Slalom
# Weave through checkpoints and obstacles

drone.takeoff()
# CP1: go up 3
for i in range(3):
    drone.forward()
# Navigate to CP2
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(3):
    drone.forward()
# Navigate to CP3
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(2):
    drone.forward()
# To CP4
drone.turn_right()
for i in range(2):
    drone.forward()
drone.turn_left()
for i in range(3):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 9: Checkpoint Slalom
drone.takeoff();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 2; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 2; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 3; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 9: Checkpoint Slalom
drone:takeoff()
for i = 1, 3 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 3 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 2 do drone:forward() end
drone:turn_right()
for i = 1, 2 do drone:forward() end
drone:turn_left()
for i = 1, 3 do drone:forward() end
drone:land()`,
    },
  },
  {
    id: 10, name: 'The Grand Challenge', description: 'Navigate the ultimate maze with 5 checkpoints',
    start: { x: 0, y: 11, dir: 0 },
    checkpoints: [{ x: 0, y: 7 }, { x: 4, y: 4 }, { x: 8, y: 7 }, { x: 8, y: 2 }, { x: 11, y: 0 }],
    obstacles: [
      { x: 2, y: 4 }, { x: 2, y: 5 }, { x: 2, y: 6 }, { x: 2, y: 7 },
      { x: 5, y: 1 }, { x: 5, y: 2 }, { x: 5, y: 3 },
      { x: 6, y: 6 }, { x: 6, y: 7 }, { x: 6, y: 8 }, { x: 6, y: 9 },
      { x: 10, y: 3 }, { x: 10, y: 4 }, { x: 10, y: 5 },
    ],
    stars: 3,
    code: {
      Python: `# Mission 10: The Grand Challenge
# Ultimate maze with 5 checkpoints!

drone.takeoff()
# CP1: straight up
for i in range(4):
    drone.forward()
# Navigate to CP2
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_left()
for i in range(3):
    drone.forward()
# Navigate to CP3
drone.turn_right()
for i in range(4):
    drone.forward()
drone.turn_right()
for i in range(3):
    drone.forward()
# Navigate to CP4
drone.turn_left()
drone.turn_left()
for i in range(5):
    drone.forward()
# Navigate to CP5
drone.turn_right()
for i in range(3):
    drone.forward()
drone.turn_left()
for i in range(2):
    drone.forward()
drone.land()`,
      JavaScript: `// Mission 10: The Grand Challenge
// Ultimate maze with 5 checkpoints!

drone.takeoff();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 4; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnLeft(); drone.turnLeft();
for (let i = 0; i < 5; i++) drone.forward();
drone.turnRight();
for (let i = 0; i < 3; i++) drone.forward();
drone.turnLeft();
for (let i = 0; i < 2; i++) drone.forward();
drone.land();`,
      Lua: `-- Mission 10: The Grand Challenge
-- Ultimate maze with 5 checkpoints!

drone:takeoff()
for i = 1, 4 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_left()
for i = 1, 3 do drone:forward() end
drone:turn_right()
for i = 1, 4 do drone:forward() end
drone:turn_right()
for i = 1, 3 do drone:forward() end
drone:turn_left() drone:turn_left()
for i = 1, 5 do drone:forward() end
drone:turn_right()
for i = 1, 3 do drone:forward() end
drone:turn_left()
for i = 1, 2 do drone:forward() end
drone:land()`,
    },
  },
];

const DIR_DELTA = [
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
];

const LANG_CLR = { Python: 'text-green-300', JavaScript: 'text-amber-300', Lua: 'text-sky-300' };
const LANG_EXT = { Python: 'py', JavaScript: 'js', Lua: 'lua' };

function CommandBlock({ cmd, index, onRemove, isActive }) {
  const type = COMMAND_TYPES.find(c => c.id === cmd);
  if (!type) return null;
  const Icon = type.icon;
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border group ${isActive ? 'ring-2 ring-yellow-400 bg-yellow-500/10' : ''}`}
      style={{ borderColor: type.color + '40', background: isActive ? undefined : type.color + '15' }}>
      <span className="text-xs font-mono text-muted-foreground w-4">{index + 1}</span>
      <Icon className="w-4 h-4" style={{ color: type.color }} />
      <span className="text-xs font-semibold" style={{ color: type.color }}>{type.label}</span>
      <button onClick={() => onRemove(index)} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
      </button>
    </motion.div>
  );
}

export default function DroneLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('drone');
  const navigate = useNavigate();
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('drone', 10);
  const [commands, setCommands] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [dronePos, setDronePos] = useState(null);
  const [droneDir, setDroneDir] = useState(0);
  const [droneFlying, setDroneFlying] = useState(false);
  const [visitedCells, setVisitedCells] = useState([]);
  const [hitCheckpoints, setHitCheckpoints] = useState([]);
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(-1);
  const cancelRef = useRef(false);
  const [language, setLanguage] = useState('Python');
  const [showCode, setShowCode] = useState(false);

  const mission = MISSIONS[currentMission];

  useEffect(() => {
    setDronePos({ x: mission.start.x, y: mission.start.y });
    setDroneDir(mission.start.dir);
    setDroneFlying(false);
    setVisitedCells([]);
    setHitCheckpoints([]);
    setResult(null);
    setCurrentStep(-1);
  }, [currentMission]);

  const addCommand = (cmdId) => { if (!isRunning) setCommands(prev => [...prev, cmdId]); };
  const removeCommand = (index) => { if (!isRunning) setCommands(prev => prev.filter((_, i) => i !== index)); };

  const clearCommands = () => {
    if (isRunning) return;
    setCommands([]);
    setDronePos({ x: mission.start.x, y: mission.start.y });
    setDroneDir(mission.start.dir);
    setDroneFlying(false);
    setVisitedCells([]);
    setHitCheckpoints([]);
    setResult(null);
    setCurrentStep(-1);
  };

  const runProgram = async () => {
    if (isRunning || commands.length === 0) return;
    cancelRef.current = false;
    setIsRunning(true);
    setResult(null);
    let pos = { x: mission.start.x, y: mission.start.y };
    let dir = mission.start.dir;
    let flying = false;
    const visited = [];
    const checkpointsHit = [];
    let error = null;
    setDronePos({ ...pos }); setDroneDir(dir); setVisitedCells([]); setHitCheckpoints([]);

    for (let i = 0; i < commands.length && !cancelRef.current; i++) {
      setCurrentStep(i);
      await new Promise(r => setTimeout(r, 400));
      const cmd = commands[i];
      if (cmd === 'takeoff') { flying = true; setDroneFlying(true); }
      else if (cmd === 'land') { flying = false; setDroneFlying(false); }
      else if (!flying) { error = 'Drone must take off first!'; break; }
      else if (cmd === 'forward') {
        const delta = DIR_DELTA[dir];
        const newX = pos.x + delta.dx, newY = pos.y + delta.dy;
        if (newX < 0 || newX >= GRID_SIZE || newY < 0 || newY >= GRID_SIZE) { error = 'Drone flew out of bounds!'; break; }
        if (mission.obstacles.some(o => o.x === newX && o.y === newY)) { error = 'Drone crashed into an obstacle!'; break; }
        pos = { x: newX, y: newY }; visited.push({ ...pos });
        setVisitedCells([...visited]); setDronePos({ ...pos });
        const cpIdx = mission.checkpoints.findIndex((cp, ci) => cp.x === pos.x && cp.y === pos.y && ci === checkpointsHit.length);
        if (cpIdx !== -1) { checkpointsHit.push(cpIdx); setHitCheckpoints([...checkpointsHit]); }
      } else if (cmd === 'left') { dir = (dir + 3) % 4; setDroneDir(dir); }
      else if (cmd === 'right') { dir = (dir + 1) % 4; setDroneDir(dir); }
    }

    setCurrentStep(-1);
    if (error) setResult({ type: 'error', message: error });
    else if (checkpointsHit.length === mission.checkpoints.length) {
      setResult({ type: 'success', message: 'Mission Complete!' });
      if (!completedMissions.includes(currentMission)) saveProgress(currentMission);
    } else setResult({ type: 'partial', message: `Reached ${checkpointsHit.length}/${mission.checkpoints.length} checkpoints. Keep trying!` });
    setIsRunning(false);
  };

  const isObstacle = (x, y) => mission.obstacles.some(o => o.x === x && o.y === y);
  const isCheckpoint = (x, y) => { const idx = mission.checkpoints.findIndex(cp => cp.x === x && cp.y === y); return idx >= 0 ? idx : -1; };
  const isVisited = (x, y) => visitedCells.some(v => v.x === x && v.y === y);

  return (
    <div className="min-h-screen bg-background" data-testid="drone-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Robotics lab about programming drone flight paths. Students use visual command blocks to navigate obstacles."
        cacheKey="lab-intro-drone" onClose={dismiss} dismissed={dismissed} onResummon={resummon} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <Rocket className="w-5 h-5 text-orange-400" />
            <span className="font-semibold text-sm">Drone Flight Programmer</span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[115px] h-7 text-xs" data-testid="language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Python">Python</SelectItem>
                <SelectItem value="JavaScript">JavaScript</SelectItem>
                <SelectItem value="Lua">Lua</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={showCode ? 'secondary' : 'outline'} size="sm" onClick={() => setShowCode(!showCode)}
              className="text-xs h-7 gap-1" data-testid="toggle-code-btn">
              <Code className="w-3 h-3" />{showCode ? 'Hide Code' : 'View Code'}
            </Button>
            <Badge variant="outline" className="text-[10px]">{completedMissions.length}/{MISSIONS.length}</Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Mission Selector */}
        <div className="flex gap-1.5 flex-wrap mb-4">
          {MISSIONS.map((m, i) => (
            <Badge key={m.id}
              variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
              className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
              onClick={() => { if (!isRunning) { clearCommands(); setCurrentMission(i); } }}
              data-testid={`mission-${m.id}`}>
              {completedMissions.includes(i) && <Star className="w-3 h-3 mr-0.5 fill-yellow-400 text-yellow-400" />}
              M{m.id}
            </Badge>
          ))}
        </div>

        {/* Mission info */}
        <motion.div key={currentMission} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
          <Target className="w-5 h-5 text-orange-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <div className={`grid gap-4 ${showCode ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-3'}`}>
          {/* Grid */}
          <div className={showCode ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card p-3 inline-block">
              <div className="grid relative"
                style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, ${CELL_SIZE}px)`, gridTemplateRows: `repeat(${GRID_SIZE}, ${CELL_SIZE}px)`, gap: '1px' }}
                data-testid="drone-grid">
                {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, idx) => {
                  const x = idx % GRID_SIZE, y = Math.floor(idx / GRID_SIZE);
                  const obstacle = isObstacle(x, y);
                  const cpIdx = isCheckpoint(x, y);
                  const visited = isVisited(x, y);
                  const isDrone = dronePos && dronePos.x === x && dronePos.y === y;
                  return (
                    <div key={idx} className={`relative flex items-center justify-center text-xs rounded-sm ${obstacle ? 'bg-red-900/50 border border-red-500/30' : visited ? 'bg-cyan-500/10' : 'bg-muted/20'}`}
                      style={{ width: CELL_SIZE, height: CELL_SIZE }}>
                      {obstacle && <div className="w-5 h-5 rounded bg-red-500/60" />}
                      {cpIdx >= 0 && !hitCheckpoints.includes(cpIdx) && (
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}
                          className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <span className="text-[9px] text-white font-bold">{cpIdx + 1}</span>
                        </motion.div>
                      )}
                      {cpIdx >= 0 && hitCheckpoints.includes(cpIdx) && (
                        <div className="w-5 h-5 rounded-full bg-green-500/30 flex items-center justify-center">
                          <span className="text-[9px] text-green-400">ok</span>
                        </div>
                      )}
                      {isDrone && (
                        <motion.div className={`absolute z-10 flex items-center justify-center ${droneFlying ? 'drop-shadow-[0_0_8px_rgba(96,165,250,0.6)]' : ''}`}
                          animate={droneFlying ? { y: [0, -3, 0] } : {}} transition={{ repeat: Infinity, duration: 0.8 }}>
                          <div className="text-xl" style={{ transform: `rotate(${droneDir * 90}deg)` }}>
                            <Rocket className="w-5 h-5 text-orange-400" />
                          </div>
                        </motion.div>
                      )}
                      {visited && !isDrone && !obstacle && cpIdx < 0 && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400/40" />}
                    </div>
                  );
                })}
              </div>
            </div>
            <AnimatePresence>
              {result && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className={`mt-3 p-3 rounded-xl border flex items-center gap-2 ${
                    result.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' :
                    result.type === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-400' :
                    'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                  }`}>
                  {result.type === 'success' ? <Star className="w-4 h-4 fill-current" /> : <Info className="w-4 h-4" />}
                  <span className="text-sm font-medium">{result.message}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Code Panel (conditionally shown) */}
          {showCode && (
            <div className="lg:col-span-1">
              <div className="rounded-xl border border-border/50 bg-[#0d1117] overflow-hidden h-full flex flex-col" data-testid="code-panel">
                <div className="px-3 py-2 bg-[#161b22] border-b border-white/10 flex items-center gap-2">
                  <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-red-500"/><div className="w-2 h-2 rounded-full bg-yellow-500"/><div className="w-2 h-2 rounded-full bg-green-500"/></div>
                  <span className="text-[10px] text-white/40 font-mono">mission_{mission.id}.{LANG_EXT[language]}</span>
                </div>
                <pre className={`flex-1 p-3 ${LANG_CLR[language]} font-mono text-[11px] leading-[18px] overflow-auto whitespace-pre`}>
                  {mission.code?.[language] || '# No code available for this mission'}
                </pre>
              </div>
            </div>
          )}

          {/* Command Panel */}
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-card border border-border/50">
              <h4 className="text-xs font-semibold mb-2 flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-yellow-400" />Commands</h4>
              <div className="grid grid-cols-1 gap-1.5">
                {COMMAND_TYPES.map(cmd => {
                  const CmdIcon = cmd.icon;
                  return (
                    <button key={cmd.id} onClick={() => addCommand(cmd.id)} disabled={isRunning}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors hover:bg-muted/50 disabled:opacity-50 text-left"
                      style={{ borderColor: cmd.color + '30' }} data-testid={`cmd-${cmd.id}`}>
                      <CmdIcon className="w-3.5 h-3.5" style={{ color: cmd.color }} />
                      <span className="text-[11px] font-semibold" style={{ color: cmd.color }}>{cmd.label}</span>
                      <span className="text-[9px] text-muted-foreground ml-auto">{cmd.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-3 rounded-xl bg-card border border-border/50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold">Program ({commands.length})</h4>
                <button onClick={clearCommands} disabled={isRunning} className="text-[10px] text-muted-foreground hover:text-destructive">Clear</button>
              </div>
              <div className="space-y-1 max-h-[260px] overflow-y-auto">
                <AnimatePresence>
                  {commands.map((cmd, i) => (
                    <CommandBlock key={`${cmd}-${i}`} cmd={cmd} index={i} onRemove={removeCommand} isActive={currentStep === i} />
                  ))}
                </AnimatePresence>
                {commands.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-3">Click commands to build your program</p>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 h-9" onClick={runProgram} disabled={isRunning || commands.length === 0} data-testid="run-drone-btn">
                <Play className="w-3.5 h-3.5 mr-1.5" />Run
              </Button>
              <Button variant="outline" className="h-9" onClick={clearCommands} disabled={isRunning} data-testid="reset-drone-btn">
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="p-3 rounded-xl bg-muted/30 border border-border/30">
              <h4 className="text-[10px] font-semibold flex items-center gap-1 mb-1 text-muted-foreground"><Info className="w-3 h-3" />How to Play</h4>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Build a sequence of commands to navigate the drone to the green checkpoint(s).
                Start with TAKEOFF, use FORWARD to move, TURN to change direction, and LAND at the end.
                Toggle "View Code" to see the equivalent script in {language}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
